import React, { useState, useEffect, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import moment from 'moment';
import TimeInput from './TimeInput';
import RecurringAvailability from './RecurringAvailability';

interface TempAvailability {
  id: string;
  tempAvailabilityId?: string;
  title: string;
  start: string;
  end: string;
  color: string;
  profile_id: number;
  eventType: 'available' | 'booked' | 'requested';
  url?: string;
  job?: any;
  practice?: any;
  editable?: boolean;
  isMerged?: boolean;
  originalEvents?: TempAvailability[];
}

interface CalendarTempAvailabilityProps {
  authenticity_token: string;
  profile_id: number;
}

const CalendarTempAvailability: React.FC<CalendarTempAvailabilityProps> = ({
  authenticity_token,
  profile_id
}) => {
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [events, setEvents] = useState<TempAvailability[]>([]);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [startTime, setStartTime] = useState<string>("8:00 AM");
  const [endTime, setEndTime] = useState<string>("5:00 PM");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
  const [confirmMessage, setConfirmMessage] = useState<string>("");
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [recurringCollapsed, setRecurringCollapsed] = useState<boolean>(true);

  // Calendar ref for accessing FullCalendar API
  const calendarRef = useRef<FullCalendar>(null);

  useEffect(() => {
    loadTempAvailabilities();
  }, []);

  // Helper functions for modal management
  const showConfirm = (message: string, action: () => void) => {
    setConfirmMessage(message);
    setConfirmAction(() => action);
    setShowConfirmModal(true);
  };

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setShowSuccessModal(true);
  };

  const showError = (message: string) => {
    setErrorMessage(message);
    setShowErrorModal(true);
  };

  const hideAllModals = () => {
    setShowConfirmModal(false);
    setShowSuccessModal(false);
    setShowErrorModal(false);

    // Close Bootstrap modals
    ['availabilityModal'].forEach(modalId => {
      const modal = document.getElementById(modalId);
      if (modal) {
        try {
          const bootstrapModal = (window as any).bootstrap.Modal.getInstance(modal);
          if (bootstrapModal) {
            bootstrapModal.hide();
          }
        } catch (error) {
          console.error(`Error closing modal ${modalId}:`, error);
        }
      }
    });
  };

  // Function to check if two events overlap
  const eventsOverlap = (event1: TempAvailability, event2: TempAvailability): boolean => {
    const start1 = new Date(event1.start);
    const end1 = new Date(event1.end);
    const start2 = new Date(event2.start);
    const end2 = new Date(event2.end);

    return start1 < end2 && start2 < end1;
  };

  // Function to merge overlapping events (only merge accepted temp requests with availability)
  const mergeOverlappingEvents = (allEvents: TempAvailability[]): TempAvailability[] => {
    const mergedEvents: TempAvailability[] = [];
    const processed = new Set<string>();

    allEvents.forEach(event => {
      if (processed.has(event.id)) return;

      const overlapping = allEvents.filter(otherEvent =>
        otherEvent.id !== event.id &&
        !processed.has(otherEvent.id) &&
        eventsOverlap(event, otherEvent) &&
        ((event.eventType === 'available' && otherEvent.eventType === 'booked') ||
          (event.eventType === 'booked' && otherEvent.eventType === 'available'))
      );

      if (overlapping.length > 0) {
        const allOverlapping = [event, ...overlapping];
        const prioritizedEvent = allOverlapping.find(e => e.eventType === 'booked') ||
          allOverlapping.find(e => e.eventType === 'available');

        if (prioritizedEvent) {
          const eventTypeSet = new Set(allOverlapping.map(e => e.eventType));
          const eventTypes = Array.from(eventTypeSet);
          let mergedTitle = prioritizedEvent.title;

          if (eventTypes.includes('booked') && eventTypes.includes('available')) {
            mergedTitle = `${prioritizedEvent.title} (Originally Available)`;
          }

          const mergedEvent: TempAvailability = {
            ...prioritizedEvent,
            title: mergedTitle,
            color: prioritizedEvent.eventType === 'booked' ? '#28a745' : '#0f4b89',
            start: new Date(Math.min(...allOverlapping.map(e => new Date(e.start).getTime()))).toISOString(),
            end: new Date(Math.max(...allOverlapping.map(e => new Date(e.end).getTime()))).toISOString(),
            isMerged: true,
            originalEvents: allOverlapping
          } as any;

          mergedEvents.push(mergedEvent);
          allOverlapping.forEach(e => processed.add(e.id));
        }
      } else {
        mergedEvents.push(event);
        processed.add(event.id);
      }
    });

    return mergedEvents;
  };

  const loadTempAvailabilities = () => {
    setEvents([]);

    const availablePromise = fetch("/temp_availabilities.json")
      .then((res) => res.ok ? res.json() : [])
      .then((result) => {
        return result.map((item: any) => {
          const rec = item.event || item;
          return {
            id: `available_${rec.id}`,
            tempAvailabilityId: String(rec.id),
            title: "Available",
            start: new Date(rec.start).toISOString(),
            end: new Date(rec.end).toISOString(),
            color: "#0f4b89",
            profile_id: rec.profile_id || profile_id,
            eventType: "available",
            editable: true
          } as TempAvailability;
        });
      })
      .catch(() => []);

    const bookedPromise = fetch("/temp_availabilities.json?booked=1")
      .then((res) => res.json())
      .then((result) => {
        return result.map((item: any) => {
          const rec = item.event || item;
          if (rec.practice) {
            return {
              id: `booked_${rec.id}`,
              tempAvailabilityId: String(rec.id),
              title: `Booked: ${rec.practice.name}`,
              start: new Date(rec.start).toISOString(),
              end: new Date(rec.end).toISOString(),
              color: "#28a745",
              profile_id: rec.profile_id || profile_id,
              eventType: "booked",
              url: `/temp_requests/${rec.id}`,
              job: rec.job,
              practice: rec.practice,
              editable: false
            } as TempAvailability;
          }
          return null;
        }).filter(Boolean);
      })
      .catch(() => []);

    const requestedPromise = fetch("/temp_availabilities.json?requested=1")
      .then((res) => res.json())
      .then((result) => {
        return result.map((item: any) => {
          const rec = item.event || item;
          return {
            id: `requested_${rec.id}`,
            tempAvailabilityId: String(rec.id),
            title: `Requested: ${rec.practice_name}`,
            start: new Date(rec.start).toISOString(),
            end: new Date(rec.end).toISOString(),
            color: "#ffc107",
            profile_id: rec.profile_id || profile_id,
            eventType: "requested",
            url: `/temp_requests/${rec.id}`,
            job: rec.id,
            practice: rec.practice_id,
            editable: false
          } as TempAvailability;
        });
      })
      .catch(() => []);

    Promise.all([availablePromise, bookedPromise, requestedPromise])
      .then(([availableEvents, bookedEvents, requestedEvents]) => {
        const allEvents = [...availableEvents, ...bookedEvents, ...requestedEvents];
        const mergedEvents = mergeOverlappingEvents(allEvents);
        setEvents(mergedEvents);
      });
  };

  const handleDayClick = (info: any) => {
    setSelectedDate(info.dateStr);
    setStartTime("8:00 AM");
    setEndTime("5:00 PM");

    setTimeout(() => {
      const modal = document.getElementById('availabilityModal');
      if (modal) {
        try {
          const bootstrapModal = new (window as any).bootstrap.Modal(modal);
          bootstrapModal.show();
        } catch (error) {
          modal.classList.add('show');
          modal.style.display = 'block';
        }
      }
    }, 100);
  };

  const convertTo24Hour = (time12: string): string => {
    const timeRegex = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i;
    const match = time12.match(timeRegex);

    if (match) {
      let hour = parseInt(match[1]);
      const minute = match[2];
      const period = match[3].toUpperCase();

      if (period === 'PM' && hour !== 12) hour += 12;
      else if (period === 'AM' && hour === 12) hour = 0;

      return `${hour.toString().padStart(2, '0')}:${minute}`;
    }
    return time12;
  };

  const handleSaveAvailability = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDate) return;

    setIsLoading(true);
    const startTime24 = convertTo24Hour(startTime);
    const endTime24 = convertTo24Hour(endTime);

    try {
      const response = await fetch('/temp_availabilities.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': authenticity_token,
        },
        body: JSON.stringify({
          temp_availability: {
            profile_id: profile_id,
            start_date: selectedDate,
            end_date: selectedDate,
            start_time: startTime24,
            end_time: endTime24
          }
        })
      });

      if (response.ok) {
        hideAllModals();
        loadTempAvailabilities();
        setSelectedDate(null);
        showSuccess("Availability saved successfully!");
      } else {
        throw new Error("Failed to save availability");
      }
    } catch (error) {
      showError("Error saving availability. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteEvent = async (eventId: string) => {
    const confirmDelete = async () => {
      try {
        const response = await fetch(`/temp_availabilities/${eventId}.json`, {
          method: 'DELETE',
          headers: { 'X-CSRF-Token': authenticity_token }
        });

        if (response.ok) {
          loadTempAvailabilities();
          showSuccess('Availability deleted successfully!');
        } else {
          throw new Error('Failed to delete event');
        }
      } catch (error) {
        showError('Error deleting availability. Please try again.');
      }
    };

    showConfirm('Are you sure you want to delete this availability?', confirmDelete);
  };

  const handleEventClick = (info: any) => {
    const event = info.event;

    if (event.extendedProps.isMerged) {
      const bookedEvent = event.extendedProps.originalEvents?.find((e: any) => e.eventType === 'booked');
      if (bookedEvent?.url) {
        window.open(bookedEvent.url, '_blank');
        return;
      }
    }

    if ((event.extendedProps.eventType === 'booked' || event.extendedProps.eventType === 'requested') && event.extendedProps.url) {
      window.open(event.extendedProps.url, '_blank');
    }
  };

  const eventRender = (info: any) => {
    const element = info.el;
    const event = info.event;

    if (event.extendedProps.eventType) {
      element.setAttribute('data-event-type', event.extendedProps.eventType);
    }

    if (event.extendedProps.isMerged) {
      element.classList.add('merged-event');
      element.style.border = "2px solid rgba(255,255,255,0.9)";
      element.style.boxShadow = "0 4px 12px rgba(0,0,0,0.2)";
    }

    const canDelete = event.extendedProps.eventType === 'available' ||
      (event.extendedProps.isMerged && event.extendedProps.originalEvents?.some((e: any) => e.eventType === 'available'));

    if (canDelete) {
      const deleteIcon = document.createElement('i');
      deleteIcon.className = 'far fa-times-circle delete-event';
      deleteIcon.style.cssText = 'position: absolute; top: 2px; right: 2px; color: white; background: rgba(220, 53, 69, 0.8); border-radius: 50%; padding: 2px; cursor: pointer; z-index: 10;';
      deleteIcon.onclick = (e) => {
        e.stopPropagation();
        e.preventDefault();
        const availableEvent = event.extendedProps.isMerged
          ? event.extendedProps.originalEvents?.find((ev: any) => ev.eventType === 'available')
          : event.extendedProps;
        handleDeleteEvent(availableEvent?.tempAvailabilityId || event.extendedProps.tempAvailabilityId || event.id);
      };
      element.style.position = 'relative';
      element.appendChild(deleteIcon);
    }
  };

  const handleRecurringComplete = () => {
    setCurrentStep(2);
    setRecurringCollapsed(true);
  };

  const handleRecurringEdit = () => {
    setRecurringCollapsed(false);
  };

  const handleBackToProfile = () => {
    window.location.href = `/profiles/${profile_id}/edit`;
  };

  // Step indicator component
  const StepIndicator = () => (
    <div className="d-flex align-items-center justify-content-center mb-4">
      <div className="d-flex align-items-center">
        <div
          className={`rounded-circle d-flex align-items-center justify-content-center`}
          style={{
            width: "32px",
            height: "32px",
            background: currentStep >= 1 ? "linear-gradient(135deg, #085ec8, #176fc9)" : "#e2e8f0",
            color: currentStep >= 1 ? "white" : "#64748b",
            fontWeight: 600,
            fontSize: "0.875rem"
          }}
        >
          {currentStep > 1 ? <i className="fa fa-check"></i> : "1"}
        </div>
        <span className={`ms-2 ${currentStep === 1 ? "fw-semibold" : "text-muted"}`} style={{ fontSize: "0.9rem" }}>
          Weekly Schedule
        </span>
      </div>
      <div
        style={{
          width: "60px",
          height: "2px",
          background: currentStep > 1 ? "#085ec8" : "#e2e8f0",
          margin: "0 1rem"
        }}
      />
      <div className="d-flex align-items-center">
        <div
          className={`rounded-circle d-flex align-items-center justify-content-center`}
          style={{
            width: "32px",
            height: "32px",
            background: currentStep >= 2 ? "linear-gradient(135deg, #085ec8, #176fc9)" : "#e2e8f0",
            color: currentStep >= 2 ? "white" : "#64748b",
            fontWeight: 600,
            fontSize: "0.875rem"
          }}
        >
          2
        </div>
        <span className={`ms-2 ${currentStep === 2 ? "fw-semibold" : "text-muted"}`} style={{ fontSize: "0.9rem" }}>
          Date Exceptions
        </span>
      </div>
    </div>
  );

  return (
    <div className="container-fluid">
      <div className="row justify-content-center">
        <div className="col-12 col-md-10 col-lg-8 col-xl-7 my-4 px-3">
          {/* Page Header */}
          <div className="text-center mb-4">
            <h1 style={{ fontSize: "1.75rem", fontWeight: 700, color: "#034ba6", marginBottom: "0.5rem" }}>
              <i className="fa fa-calendar-alt me-2"></i>
              Manage Your Availability
            </h1>
            <p className="text-muted" style={{ fontSize: "0.95rem" }}>
              Set your weekly schedule and add specific date exceptions
            </p>
          </div>

          {/* Step Indicator */}
          <StepIndicator />

          {/* Step 1: Recurring Weekly Schedule */}
          {currentStep === 1 && (
            <div
              className="form-section-card"
              style={{
                background: "white",
                padding: "2rem",
                borderRadius: "16px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)"
              }}
            >
              <div className="mb-4">
                <h2 style={{ fontSize: "1.25rem", fontWeight: 600, color: "#333", marginBottom: "0.5rem" }}>
                  <i className="fa fa-calendar-week me-2" style={{ color: "#085ec8" }}></i>
                  Your Recurring Weekly Schedule
                </h2>
                <p className="text-muted mb-0" style={{ fontSize: "0.9rem" }}>
                  Set your default availability for each week. This is what practices will see as your general availability.
                </p>
              </div>

              <RecurringAvailability
                profileId={profile_id}
                alwaysExpanded={true}
                onComplete={handleRecurringComplete}
              />

              <div className="d-flex justify-content-between align-items-center mt-4 pt-3" style={{ borderTop: "1px solid #f0f0f0" }}>
                <button
                  className="btn-modern btn-modern--ghost"
                  onClick={() => setCurrentStep(2)}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#6c757d",
                    padding: "0.5rem 1rem",
                    fontSize: "0.9rem",
                    cursor: "pointer"
                  }}
                >
                  Skip for now
                  <i className="fa fa-arrow-right ms-2"></i>
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Specific Date Exceptions */}
          {currentStep === 2 && (
            <>
              {/* Collapsed Recurring Summary */}
              {recurringCollapsed ? (
                <RecurringAvailability
                  profileId={profile_id}
                  collapsed={true}
                  onEdit={handleRecurringEdit}
                />
              ) : (
                <div
                  className="form-section-card mb-3"
                  style={{
                    background: "white",
                    padding: "1.5rem",
                    borderRadius: "16px",
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)"
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h3 style={{ fontSize: "1rem", fontWeight: 600, color: "#333", margin: 0 }}>
                      <i className="fa fa-calendar-week me-2" style={{ color: "#085ec8" }}></i>
                      Edit Weekly Schedule
                    </h3>
                    <button
                      onClick={() => setRecurringCollapsed(true)}
                      style={{
                        background: "transparent",
                        border: "none",
                        color: "#6c757d",
                        cursor: "pointer",
                        padding: "0.25rem"
                      }}
                    >
                      <i className="fa fa-times"></i>
                    </button>
                  </div>
                  <RecurringAvailability
                    profileId={profile_id}
                    alwaysExpanded={true}
                    onComplete={() => setRecurringCollapsed(true)}
                  />
                </div>
              )}

              {/* Specific Dates Section */}
              <div
                className="form-section-card"
                style={{
                  background: "white",
                  padding: "2rem",
                  borderRadius: "16px",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)"
                }}
              >
                <div className="mb-4">
                  <h2 style={{ fontSize: "1.25rem", fontWeight: 600, color: "#333", marginBottom: "0.5rem" }}>
                    <i className="fa fa-calendar-plus me-2" style={{ color: "#085ec8" }}></i>
                    Specific Date Exceptions
                  </h2>
                  <p className="text-muted mb-0" style={{ fontSize: "0.9rem" }}>
                    Add availability for specific dates that differ from your weekly schedule. Click on a date to add your hours.
                  </p>
                </div>

                {/* Legend */}
                <div className="d-flex flex-wrap gap-3 mb-4 p-3" style={{ background: "#f8f9fa", borderRadius: "8px" }}>
                  <div className="d-flex align-items-center gap-2">
                    <div style={{ width: "12px", height: "12px", background: "#0f4b89", borderRadius: "3px" }}></div>
                    <span className="small text-muted">Available</span>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <div style={{ width: "12px", height: "12px", background: "#28a745", borderRadius: "3px" }}></div>
                    <span className="small text-muted">Booked</span>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <div style={{ width: "12px", height: "12px", background: "#ffc107", borderRadius: "3px" }}></div>
                    <span className="small text-muted">Requested</span>
                  </div>
                </div>

                {/* Calendar */}
                <div className="calendar-temp-availability">
                  <FullCalendar
                    ref={calendarRef}
                    plugins={[dayGridPlugin, interactionPlugin]}
                    dateClick={handleDayClick}
                    eventClick={handleEventClick}
                    events={events}
                    eventDidMount={eventRender}
                    editable={false}
                    droppable={false}
                    eventStartEditable={false}
                    eventDurationEditable={false}
                    timeZone="local"
                    displayEventTime={true}
                    eventTimeFormat={{
                      hour: 'numeric',
                      minute: '2-digit',
                      meridiem: 'short'
                    }}
                    headerToolbar={{
                      left: 'prev,next today',
                      center: 'title',
                      right: ''
                    }}
                    height="auto"
                    dayMaxEvents={3}
                    moreLinkClick={() => {}}
                  />
                </div>

                {/* Navigation */}
                <div className="d-flex justify-content-between align-items-center mt-4 pt-3" style={{ borderTop: "1px solid #f0f0f0" }}>
                  <button
                    onClick={() => setCurrentStep(1)}
                    style={{
                      background: "transparent",
                      border: "2px solid #e8ecef",
                      color: "#085ec8",
                      padding: "0.625rem 1.25rem",
                      borderRadius: "0.5rem",
                      fontSize: "0.95rem",
                      fontWeight: 600,
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.5rem"
                    }}
                  >
                    <i className="fa fa-arrow-left"></i>
                    Back
                  </button>
                  <button
                    onClick={handleBackToProfile}
                    style={{
                      background: "linear-gradient(135deg, #71ad08, #5f9406)",
                      color: "white",
                      border: "none",
                      padding: "0.625rem 1.5rem",
                      borderRadius: "0.5rem",
                      fontSize: "0.95rem",
                      fontWeight: 600,
                      cursor: "pointer",
                      boxShadow: "0 4px 12px rgba(113, 173, 8, 0.3)",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.5rem"
                    }}
                  >
                    <i className="fa fa-check"></i>
                    Done - Return to Profile
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Bootstrap Modal for adding specific date availability */}
      <div className="modal fade" id="availabilityModal" tabIndex={-1} aria-labelledby="availabilityModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content" style={{ borderRadius: "16px", overflow: "hidden" }}>
            <div className="modal-header" style={{ background: "linear-gradient(135deg, #085ec8, #176fc9)", color: "white", border: "none" }}>
              <h5 className="modal-title" id="availabilityModalLabel">
                <i className="fa fa-clock me-2"></i>
                Add Availability for {selectedDate ? moment(selectedDate).format('MMMM Do, YYYY') : 'Selected Date'}
              </h5>
              <button type="button" className="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>

            <form onSubmit={handleSaveAvailability}>
              <div className="modal-body p-4">
                <div className="row">
                  <div className="col-md-6">
                    <TimeInput
                      value={startTime}
                      onChange={setStartTime}
                      label="Start Time"
                      icon="fa-play"
                      placeholder="e.g. 8am, 8:00 AM"
                    />
                  </div>
                  <div className="col-md-6">
                    <TimeInput
                      value={endTime}
                      onChange={setEndTime}
                      label="End Time"
                      icon="fa-stop"
                      placeholder="e.g. 5pm, 5:00 PM"
                    />
                  </div>
                </div>
              </div>

              <div className="modal-footer" style={{ border: "none", background: "#f8f9fa" }}>
                <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  style={{
                    background: "linear-gradient(135deg, #085ec8, #176fc9)",
                    color: "white",
                    border: "none",
                    padding: "0.5rem 1.25rem",
                    borderRadius: "0.5rem",
                    fontWeight: 600,
                    cursor: "pointer"
                  }}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-save me-2"></i>
                      Save
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <>
          <div className="modal-backdrop fade show" style={{ zIndex: 1040 }}></div>
          <div className="modal fade show" style={{ display: 'block', zIndex: 1050 }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content" style={{ borderRadius: "16px" }}>
                <div className="modal-header bg-warning text-dark">
                  <h5 className="modal-title">
                    <i className="fa fa-question-circle me-2"></i>Confirm
                  </h5>
                </div>
                <div className="modal-body">
                  <p className="mb-0">{confirmMessage}</p>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowConfirmModal(false)}>Cancel</button>
                  <button className="btn btn-warning" onClick={() => { setShowConfirmModal(false); if (confirmAction) confirmAction(); }}>
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <>
          <div className="modal-backdrop fade show" style={{ zIndex: 1040 }}></div>
          <div className="modal fade show" style={{ display: 'block', zIndex: 1050 }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content" style={{ borderRadius: "16px" }}>
                <div className="modal-header bg-success text-white">
                  <h5 className="modal-title">
                    <i className="fa fa-check-circle me-2"></i>Success
                  </h5>
                </div>
                <div className="modal-body">
                  <p className="mb-0">{successMessage}</p>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-success" onClick={() => setShowSuccessModal(false)}>OK</button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <>
          <div className="modal-backdrop fade show" style={{ zIndex: 1040 }}></div>
          <div className="modal fade show" style={{ display: 'block', zIndex: 1050 }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content" style={{ borderRadius: "16px" }}>
                <div className="modal-header bg-danger text-white">
                  <h5 className="modal-title">
                    <i className="fa fa-exclamation-triangle me-2"></i>Error
                  </h5>
                </div>
                <div className="modal-body">
                  <p className="mb-0">{errorMessage}</p>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-danger" onClick={() => setShowErrorModal(false)}>Close</button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CalendarTempAvailability;
